import * as React from "react";
import {
  addBulkPartMatchedProducts,
  addDmsParts,
  useGetNotSyncedDmsPartsMutation,
} from "../../../redux/reducer/features/Integration/DmsSetupSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../redux/reducer/rootReducer";
import { LocationPart } from "../../../models/features/Integration/DmsSetup";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  debounce,
  FormControlLabel,
  Switch,
  TextField,
} from "@mui/material";
import ServerAppGrid from "../../app/ServerAppGrid";
import {
  GridColDef,
  GridFilterInputValueProps,
  GridFilterItem,
  GridFilterOperator,
  GridRenderCellParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { AppFilter } from "../../../models/app/AppModels";
import { useGetDmsManufacturersQuery } from "../../../redux/reducer/features/ShoppingPortal/ShoppingPortalSlice";
import { DmsPartRequestDto } from "../../../models/features/ShoppingPortal/DmsPart";
import { AppGridId } from "../../../models/app/AppGridModels";
import {
  capitalizeFirstLetter,
  GetAppGridColumns,
} from "../../../services/utilities/utility";
import { setHasUnsavedChanges } from "../../../redux/reducer/features/App/AppGridSlice";

interface DmsPartsProps {
  handleNext: any;
}

const DmsParts: React.FunctionComponent<DmsPartsProps> = ({ handleNext }) => {
  const PartgridSetting = useSelector((state: AppState) =>
    state?.AppGridReducer?.setting.find(
      (x) => x.gridId === AppGridId.Initiate_Parts_DMS_Parts
    )
  );
  const selectedProducts = useSelector(
    (state: AppState) => state.dmsReducer.dmsParts
  );
  const locations = useSelector(
    (state: AppState) => state.authReducer.dmsSetup?.dmsLocations
  );
  const { data: dmsManufacturersList } = useGetDmsManufacturersQuery({
    dealerId: locations ? locations[0].dealerId.toString() ?? "" : "",
  });

  const dispatch = useDispatch();
  const [totalRecords, setTotalRecords] = React.useState(0);
  const [paging, setPaging] = React.useState<AppFilter>({
    pageIndex: 0,
    pageSize: 100,
    totalRecords: 0,
    items: [],
    quickFilterValues: [],
    virtualShowroomProducts: false,
  });
  const [stockingFlag, setStockingFlag] = React.useState(false);
  const [getNotSyncedDmsPartsMutation, getNotSyncedDmsPartsMutationResult] =
    useGetNotSyncedDmsPartsMutation();
  const [dmsParts, setDmsParts] = React.useState<LocationPart[]>([]);
  const { dmsSetup } = useSelector((state: AppState) => state.authReducer);
  React.useEffect(() => {
    fetchDmsPartsData();
  }, [paging]);

  React.useEffect(() => {
    dispatch(
      setHasUnsavedChanges(
        selectedProducts ? selectedProducts.length > 0 : false
      )
    );
  }, []);

  React.useEffect(() => {
    setPaging({ ...paging, pageIndex: 0 });
  }, [paging.items, paging.quickFilterValues, stockingFlag]);

  const fetchDmsPartsData = () => {
    var mfgFilter = "";
    var inventoryPartId = "";
    var dmsMfrId = "";
    if (paging.items.some((n) => n.columnField === "inventoryPartId")) {
      inventoryPartId =
        paging.items.find((n) => n.columnField === "inventoryPartId")?.value ??
        "";
    }

    if (paging.items.some((n) => n.columnField === "mfrName")) {
      dmsMfrId =
        paging.items.find((n) => n.columnField === "mfrName")?.value ?? "";
    }
    var data: DmsPartRequestDto = {
      dealerIds: dmsSetup?.dmsLocations.map((n) => n.dealerId.toString()) ?? [],
      pageSize: paging.pageSize,
      pageNumber: paging.pageIndex,
      stockingOnly: stockingFlag ? true : false,
      searchPartNumber: "",
      searchPartDescription: "",
      search: paging.quickFilterValues.join(" "),
      searchManufacturerId: dmsMfrId,
      searchManufacturerCode: mfgFilter.length > 0 ? [mfgFilter] : [],
      searchManufacturerCodeNotIn: [],
      inventoryPartIdNotIn: [],
      searchPartId: [inventoryPartId],
    };
    getNotSyncedDmsPartsMutation({ data: data }).then((response: any) => {
      if (response?.data && response?.data?.entityList !== null) {
        setDmsParts(response?.data?.entityList);
        if (paging.pageIndex === 0) {
          setTotalRecords(response.data.totalRecords);
        }
      }
    });
  };
  const handleItemSelect = (items: any[]) => {
    dispatch(setHasUnsavedChanges(items.length > 0));
    const updatedSelectedProducts =
      selectedProducts?.filter((sp) => items.includes(sp.inventoryPartId)) ??
      [];

    dmsParts.forEach((item) => {
      if (
        items.includes(item.inventoryPartId) &&
        !selectedProducts?.some(
          (sp) => sp.inventoryPartId === item.inventoryPartId
        )
      ) {
        updatedSelectedProducts.push(item);
      }
    });
    dispatch(
      addDmsParts({
        parts: updatedSelectedProducts,
      })
    );
  };
  const handlePartNext = () => {
    dispatch(
      addBulkPartMatchedProducts(
        selectedProducts?.map((n) => ({
          dmsPart: n,
          isManuallyMatched: true,
        })) ?? []
      )
    );
    handleNext();
  };
  function CustomFilterInputValue(props: GridFilterInputValueProps) {
    const { item, applyValue } = props;
    const customFilterRef: React.Ref<any> = React.useRef(null);
    const [inputValue, setInputValue] = React.useState(item.value || "");

    const handleSearchTextChange = React.useCallback(
      debounce((newValue: string) => {
        applyValue({ ...item, value: newValue });
      }, 1000),
      [applyValue, item]
    );

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setInputValue(newValue);
      handleSearchTextChange(newValue);
    };

    React.useEffect(() => {
      setInputValue(item.value || "");
    }, [item.value]);
    return (
      <TextField
        name="mfrFilterValue"
        onChange={handleInputChange}
        ref={customFilterRef}
        size="small"
        value={inputValue}
        variant="standard"
        label="Value"
        sx={{ pt: 0.4 }}
      ></TextField>
    );
  }
  const customFilterOperators: GridFilterOperator[] = [
    {
      label: "Equals",
      value: "equals",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (
          !filterItem.columnField ||
          !filterItem.value ||
          !filterItem.operatorValue
        ) {
          return null;
        }

        return (params): boolean => {
          return params.value == filterItem.value;
        };
      },
      InputComponent: CustomFilterInputValue,
    },
  ];

  function CustomMfrNameFilterInputValue(props: GridFilterInputValueProps) {
    const { item, applyValue } = props;

    const handleDropdownFilterChange = (event: any, newValue: any) => {
      applyValue({
        ...item,
        value: newValue ? newValue.manufacturerId : undefined,
      });
    };
    return (
      <Autocomplete
        options={dmsManufacturersList?.entityList ?? []}
        getOptionLabel={(mfr) =>
          `${capitalizeFirstLetter(mfr.name !== null ? mfr.name : "")} - ${
            mfr.code
          }`
        }
        value={
          dmsManufacturersList?.entityList?.find(
            (mfr) => mfr.manufacturerId === item.value
          ) || null
        }
        onChange={handleDropdownFilterChange}
        renderInput={(params) => (
          <TextField
            {...params}
            name="mfrFilterValue"
            size="small"
            variant="standard"
            label="Manufacturer"
            sx={{ pt: 0.4 }}
          />
        )}
        PaperComponent={({ children }) => (
          <Box
            sx={{
              width: 320,
              backgroundColor: "white",
              boxShadow: 3,
              borderRadius: 1,
              mt: 1,
              "& .MuiAutocomplete-listbox": {
                maxHeight: 300,
                overflow: "auto",
              },
            }}
          >
            {children}
          </Box>
        )}
      />
    );
  }

  const customMfrNameFilterOperator: GridFilterOperator[] = [
    {
      label: "Equals",
      value: "equals",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (
          !filterItem.columnField ||
          !filterItem.value ||
          !filterItem.operatorValue
        ) {
          return null;
        }

        return (params): boolean => {
          return params.value == filterItem.value;
        };
      },
      InputComponent: CustomMfrNameFilterInputValue,
    },
  ];

  const columns: GridColDef[] = [
    {
      field: "mfrCode",
      headerName: "Mfr Code",
      flex: 1,
      filterable: false,
    },
    {
      field: "mfrName",
      headerName: "Mfr Name",
      flex: 1,
      filterOperators: customMfrNameFilterOperator,
    },
    {
      field: "inventoryPartId",
      headerName: "Part Id",
      flex: 1,
      filterOperators: customFilterOperators,
    },
    {
      field: "partNumber",
      headerName: "Part Number",
      flex: 1,
      filterable: false,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      filterable: false,
    },
    {
      field: "price",
      headerName: "Price ($)",
      headerAlign: "right",
      flex: 1,
      type: "number",
      filterable: false,
      align: "right",
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.price.toFixed(2),
    },
    {
      field: "averageCost",
      headerName: "Cost ($)",
      headerAlign: "right",
      flex: 1,
      align: "right",
      type: "number",
      filterable: false,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.averageCost.toFixed(2),
    },
    {
      field: "stockItem",
      headerName: "Stocking Type",
      headerAlign: "center",
      flex: 1,
      align: "center",
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        return params?.row?.stockItem ? (
          <Chip label="Stocking" color="success" size="small" />
        ) : (
          <Chip label="Non Stocking" color="warning" size="small" />
        );
      },
    },
    {
      field: "availableQty",
      headerName: "Available Qty",
      flex: 1,
      type: "number",
      filterable: false,
    },
  ];

  return (
    <>
      <Box height={590}>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          gap={2}
        >
          <Alert severity="info">
            Selected items will be synced to your website and listed in the
            Product Manager.
          </Alert>
          <Box>
            <Button
              size="small"
              variant="contained"
              disabled={selectedProducts?.length === 0}
              onClick={handlePartNext}
            >
              Next
            </Button>
          </Box>
        </Box>
        <Box sx={{ width: "100%" }} textAlign="right">
          <FormControlLabel
            control={
              <Switch
                name="jason"
                value={stockingFlag}
                onChange={() => {
                  handleItemSelect([]);
                  setStockingFlag(!stockingFlag);
                }}
                size="small"
              />
            }
            label="Stocking Only"
          />
        </Box>
        <Box height={600} pt={1}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <ServerAppGrid
              gridId={AppGridId.Initiate_Parts_DMS_Parts}
              getRowId={(row) => row.inventoryPartId}
              data={dmsParts}
              columns={GetAppGridColumns(
                columns,
                PartgridSetting?.columnVisibilityModel
              )}
              isLoading={getNotSyncedDmsPartsMutationResult.isLoading}
              paging={paging}
              setPaging={setPaging}
              totalRecords={totalRecords}
              height={535}
              selectedRows={selectedProducts?.map((n) => n.inventoryPartId)}
              onSelect={(newSelection: any) => {
                handleItemSelect(newSelection);
              }}
              keepNonExistentRowsSelected={true}
              hidePageSize={true}
            ></ServerAppGrid>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default DmsParts;
